import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import HelmetMetaTag from "../Utils/HelmetMetaTag/HelmetMetaTag";
import blogService from "./blogService";

const BlogCard = ({ post, onReadMore }) => {
  const postUrl = `/blog/${post.slug || post.id}`;

  return (
    <article className="cric-blog-card" id={`post-${post.id}`}>
      <div
        className="cric-blog-card-media"
        onClick={() => onReadMore(post)}
        style={{ cursor: "pointer" }}
      >
        <img src={post.image} alt={post.title} loading="lazy" />
      </div>
      <div className="cric-blog-card-body">
        <div className="cric-blog-card-tags">
          {post.category.slice(0, 3).map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
        <h3
          onClick={() => onReadMore(post)}
          style={{ cursor: "pointer" }}
          title={post.title}
        >
          <a
            href={postUrl}
            onClick={(e) => {
              e.preventDefault();
              onReadMore(post);
            }}
            style={{ color: "inherit", textDecoration: "none" }}
          >
            {post.title}
          </a>
        </h3>
        <p>{post.summary}</p>
        <div className="cric-blog-card-footer">
          <time>{post.date}</time>
          <a
            href={postUrl}
            className="cric-blog-read-btn"
            onClick={(e) => {
              e.preventDefault();
              onReadMore(post);
            }}
          >
            Read More
          </a>
        </div>
      </div>
    </article>
  );
};

const SkeletonCard = () => (
  <div className="cric-blog-card cric-blog-skeleton-card">
    <div className="cric-blog-card-media cric-blog-skeleton-media" />
    <div className="cric-blog-card-body">
      <div className="cric-blog-skeleton-line short" />
      <div className="cric-blog-skeleton-line title" />
      <div className="cric-blog-skeleton-line title short" />
      <div className="cric-blog-skeleton-line body" />
      <div className="cric-blog-skeleton-line body" />
    </div>
  </div>
);

export default function BlogPage() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [categoriesList, setCategoriesList] = useState(["All Post"]);
  const [category, setCategory] = useState("All Post");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch WP blog listing & categories on component mount
  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    const fetchWpBlogData = async () => {
      try {
        const [postsData, catsData] = await Promise.allSettled([
          blogService.getBlogPosts(),
          blogService.getBlogCategories(),
        ]);

        if (!isMounted) return;

        if (postsData.status === "fulfilled" && postsData.value.posts) {
          const fetchedPosts = postsData.value.posts;
          setPosts(fetchedPosts);

          // Extract category names from posts
          const extractedCats = Array.from(
            new Set(fetchedPosts.flatMap((p) => p.category))
          );

          if (catsData.status === "fulfilled" && Array.isArray(catsData.value)) {
            const apiCatNames = catsData.value
              .map((c) => c.name)
              .filter((name) => name && name !== "Uncategorized");
            const combinedCats = Array.from(
              new Set(["All Post", ...extractedCats, ...apiCatNames])
            );
            setCategoriesList(combinedCats);
          } else {
            setCategoriesList(["All Post", ...extractedCats]);
          }
        } else {
          throw new Error("Failed to load WordPress posts.");
        }
      } catch (err) {
        if (isMounted) {
          console.error("WP API Fetch Error:", err);
          setError("Failed to load live blog posts. Showing latest content.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchWpBlogData();

    return () => {
      isMounted = false;
    };
  }, []);

  // First object from WP API response array as Hero Section
  const featuredPost = useMemo(() => posts[0] || null, [posts]);

  // Filter posts for grid layout (shows all posts in grid)
  const filteredPosts = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    return posts.filter((post) => {
      const matchesCategory =
        category === "All Post" || post.category.includes(category);
      const matchesSearch =
        !normalizedSearch ||
        post.title.toLowerCase().includes(normalizedSearch) ||
        post.summary.toLowerCase().includes(normalizedSearch) ||
        post.category.join(" ").toLowerCase().includes(normalizedSearch);

      return matchesCategory && matchesSearch;
    });
  }, [posts, category, searchText]);

  // Dynamic Navigation to Blog Detail URL (/blog/:slug or /blog/:id)
  const handleOpenDetail = (post) => {
    if (post) {
      const targetIdentifier = post.slug || post.id;
      navigate(`/blog/${targetIdentifier}`);
    }
  };

  return (
    <React.Fragment>
      <HelmetMetaTag
        titleName="CricBuddy Blog | Sports Venue Booking Tips"
        desc="Read CricBuddy blog articles on sports venue booking, cricket turf tips, packages, events, and venue owner growth."
        canonical={window.location.href}
        ogtitle="CricBuddy Blog | Sports Venue Booking Tips"
        ogdesc="Read CricBuddy blog articles on sports venue booking, cricket turf tips, packages, events, and venue owner growth."
        ogtype="website"
        ogurl={window.location.href}
        ogimg="https://cricbuddy.in/logo192.png"
      />

      <section className="cric-blog-page">
        {/* Featured Hero Banner - Uses 1st object from WP API */}
        {featuredPost && (
          <div className="cric-blog-hero">
            <div className="cric-blog-hero-image">
              <img
                src={featuredPost.image}
                alt={featuredPost.title}
                onClick={() => handleOpenDetail(featuredPost)}
                style={{ cursor: "pointer" }}
              />
            </div>
            <div className="cric-blog-hero-dots" aria-hidden="true"></div>
            <div className="cric-blog-hero-panel">
              {featuredPost.category && featuredPost.category.length > 0 && (
                <div className="cric-blog-card-tags" style={{ marginBottom: "10px" }}>
                  {featuredPost.category.slice(0, 2).map((item) => (
                    <span key={item}>{item}</span>
                  ))}
                </div>
              )}
              <h1>
                <a
                  href={`/blog/${featuredPost.slug || featuredPost.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleOpenDetail(featuredPost);
                  }}
                  style={{ color: "inherit", textDecoration: "none" }}
                >
                  {featuredPost.title}
                </a>
              </h1>
              {featuredPost.summary && (
                <p className="cric-blog-hero-summary">{featuredPost.summary}</p>
              )}
              <div className="cric-blog-hero-meta">
                <time>{featuredPost.date}</time>
                <a
                  href={`/blog/${featuredPost.slug || featuredPost.id}`}
                  className="cric-blog-read-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    handleOpenDetail(featuredPost);
                  }}
                >
                  Read More &rarr;
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Section Header */}
        <div className="cric-blog-title-wrap">
          <h2>CricBuddy Blog</h2>
          <span aria-hidden="true"></span>
        </div>

        {/* Filter Toolbar */}
        <div className="cric-blog-toolbar">
          <label className="cric-blog-select-label">
            <span className="visually-hidden">Filter blog posts</span>
            <select
              value={category}
              onChange={(event) => setCategory(event.target.value)}
            >
              {categoriesList.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>

          <label className="cric-blog-search">
            <img src="/images/searchicon.svg" alt="" />
            <input
              type="search"
              placeholder="Search Blog"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
            />
          </label>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="cric-blog-grid">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        )}

        {/* Error Notification */}
        {error && !loading && (
          <div className="cric-blog-error-banner">
            <p>{error}</p>
          </div>
        )}

        {/* Blog Post Grid */}
        {!loading && filteredPosts.length > 0 && (
          <div className="cric-blog-grid">
            {filteredPosts.map((post) => (
              <BlogCard key={post.id} post={post} onReadMore={handleOpenDetail} />
            ))}
          </div>
        )}

        {/* Empty Search Result State */}
        {!loading && filteredPosts.length === 0 && (
          <div className="cric-blog-empty">
            No blog posts found. Try selecting another category or search term.
          </div>
        )}
      </section>
    </React.Fragment>
  );
}
